// src/utils/ui.js
import { OIL_COLORS } from "../constants";

// ── FORMATTERS ───────────────────────────────────────────────────────
export const fmt    = n => "₹" + Math.round(Number(n) || 0).toLocaleString("en-IN");
export const fmtL   = n => (Number(n) || 0).toFixed(1) + " L";
export const fmtKg  = n => (Number(n) || 0).toFixed(1) + " kg";
export const today  = () => new Date().toISOString().slice(0, 10);
export const fmtTime = ts => {
  const d = new Date(Number(ts));
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
};
export const fmtDate = d => {
  if (!d) return "";
  const dt = new Date(d);
  return dt.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

// ── STYLES ───────────────────────────────────────────────────────────
export const S = {
  card: {
    background: "#fff",
    borderRadius: 12,
    border: "1px solid #E8E4DC",
    padding: "12px 14px",
    marginBottom: 10,
  },
  cardTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#888",
    textTransform: "uppercase",
    letterSpacing: "0.8px",
    marginBottom: 10,
  },
  label: {
    fontSize: 12,
    color: "#888",
    marginBottom: 4,
    display: "block",
  },
  input: {
    width: "100%",
    border: "1px solid #DDD9D0",
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 14,
    background: "#FAFAF8",
    outline: "none",
    marginBottom: 8,
    fontFamily: "'DM Sans', sans-serif",
    boxSizing: "border-box",
  },
  select: {
    width: "100%",
    border: "1px solid #DDD9D0",
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 14,
    background: "#FAFAF8",
    outline: "none",
    marginBottom: 8,
    fontFamily: "'DM Sans', sans-serif",
    boxSizing: "border-box",
  },
  btn: (bg = "#27500A", color = "#C0DD97") => ({
    background: bg,
    color,
    border: "none",
    borderRadius: 8,
    padding: "11px 20px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    width: "100%",
    fontFamily: "'DM Sans', sans-serif",
    transition: "opacity 0.15s",
  }),
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 0",
    borderBottom: "1px solid #F0EDE6",
  },
  infoBox: {
    background: "#F0F9E8",
    borderRadius: 8,
    padding: "10px 12px",
    marginBottom: 10,
    fontSize: 13,
  },
  errBox: {
    color: "#A32D2D",
    fontSize: 12,
    marginBottom: 8,
  },
  successMsg: {
    color: "#27500A",
    fontSize: 13,
    marginBottom: 8,
    fontWeight: 500,
  },
  skuBtn: (active) => ({
    padding: "7px 14px",
    borderRadius: 8,
    fontSize: 13,
    cursor: "pointer",
    border: `1px solid ${active ? "#27500A" : "#E0DDD5"}`,
    background: active ? "#27500A" : "#F7F4EE",
    color: active ? "#C0DD97" : "#555",
    userSelect: "none",
  }),
  payBtn: (active, mode) => {
    const colors = {
      Cash:   ["#EAF3DE", "#27500A"],
      UPI:    ["#E6F1FB", "#185FA5"],
      Credit: ["#FAEEDA", "#854F0B"],
    };
    const [bg, fg] = colors[mode] || ["#F7F4EE", "#555"];
    return {
      padding: "7px 16px",
      borderRadius: 8,
      fontSize: 13,
      cursor: "pointer",
      border: `1px solid ${active ? fg : "#E0DDD5"}`,
      background: active ? bg : "#F7F4EE",
      color: active ? fg : "#555",
      userSelect: "none",
    };
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#888",
    textTransform: "uppercase",
    letterSpacing: "0.7px",
    margin: "8px 0",
  },
};

// ── COMPONENTS ───────────────────────────────────────────────────────
export function OilDot({ oil, size = 8 }) {
  return (
    <span style={{
      width: size, height: size, borderRadius: "50%",
      background: OIL_COLORS[oil] || "#888",
      display: "inline-block", marginRight: 6, flexShrink: 0,
    }} />
  );
}

export function PayPill({ mode }) {
  const styles = {
    Cash:   { bg: "#EAF3DE", color: "#27500A" },
    UPI:    { bg: "#E6F1FB", color: "#185FA5" },
    Credit: { bg: "#FAEEDA", color: "#854F0B" },
  };
  const { bg, color } = styles[mode] || { bg: "#F7F4EE", color: "#888" };
  return (
    <span style={{
      fontSize: 10, padding: "2px 8px", borderRadius: 8,
      background: bg, color, marginLeft: 4, display: "inline-block",
    }}>{mode}</span>
  );
}

export function StatusPill({ ok, label }) {
  return (
    <span style={{
      fontSize: 10, padding: "2px 8px", borderRadius: 8,
      background: ok ? "#EAF3DE" : "#FAECE7",
      color: ok ? "#27500A" : "#993C1D",
    }}>{label}</span>
  );
}

export function MetCard({ label, value, sub, accent = "#27500A" }) {
  return (
    <div style={{
      background: "#F7F4EE", borderRadius: 10, padding: "10px 12px",
      borderLeft: `3px solid ${accent}`,
    }}>
      <div style={{ fontSize: 11, color: "#888", marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: "#1a1a1a" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export function AlertBar({ text, type = "warning" }) {
  const styles = {
    warning: { bg: "#FFF3CD", border: "#FAC775", color: "#633806" },
    info:    { bg: "#E6F1FB", border: "#85B7EB", color: "#0C447C" },
    success: { bg: "#EAF3DE", border: "#C0DD97", color: "#27500A" },
    error:   { bg: "#FCEBEB", border: "#F7C1C1", color: "#A32D2D" },
  };
  const { bg, border, color } = styles[type];
  return (
    <div style={{
      background: bg, border: `1px solid ${border}`, borderRadius: 8,
      padding: "8px 12px", marginBottom: 10, fontSize: 12, color,
      display: "flex", alignItems: "center", gap: 6,
    }}>
      {type === "warning" && "⚠️"}
      {type === "info"    && "ℹ️"}
      {type === "success" && "✓"}
      {type === "error"   && "✕"}
      {text}
    </div>
  );
}

export function EmptyState({ text }) {
  return (
    <div style={{
      color: "#aaa", fontSize: 13, textAlign: "center",
      padding: "24px 0",
    }}>{text}</div>
  );
}

export function TwoCol({ children }) {
  return (
    <div style={{
      display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8,
    }}>
      {children}
    </div>
  );
}

export function TabRow({ tabs, active, onChange }) {
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
      {tabs.map(t => (
        <button key={t.id} onClick={() => onChange(t.id)}
          style={{
            flex: 1, padding: "9px", borderRadius: 8, fontSize: 13, fontWeight: 600,
            cursor: "pointer", border: "1px solid #E0DDD5",
            background: active === t.id ? "#27500A" : "#F7F4EE",
            color: active === t.id ? "#C0DD97" : "#555",
            fontFamily: "'DM Sans', sans-serif",
          }}>
          {t.label}
        </button>
      ))}
    </div>
  );
}
